/**
 * AuthContext.tsx — Contexto de autenticação do Órbita.
 *
 * Comportamento fail-closed (ADR-003 §5):
 *   - Usuário sem claim product:"orbita" → status "no-access"
 *   - Usuário sem tenantId no claim → status "no-access"
 *   - Enquanto carrega → status "loading" (nunca concede acesso prematuramente)
 *
 * Claims lidos via getIdTokenResult() — sempre do token JWT, nunca do cliente.
 * Refresh de token ocorre automaticamente ao mudar onAuthStateChanged.
 */

import {
  createContext,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from "react";
import {
  onAuthStateChanged,
  signOut as firebaseSignOut,
  sendPasswordResetEmail,
  type User,
} from "firebase/auth";
import { auth } from "@/lib/firebase";
import type {
  AuthState,
  OrbClaims,
  OrbModuleId,
  OrbRole,
  OrbUser,
} from "./auth.types";

// ---------------------------------------------------------------------------
// Helpers de validação de claims
// ---------------------------------------------------------------------------

function isOrbRole(value: unknown): value is OrbRole {
  return value === "admin" || value === "financeiro" || value === "viewer";
}

function isStringArray(value: unknown): value is string[] {
  return Array.isArray(value) && value.every((v) => typeof v === "string");
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

/**
 * Extrai e valida os custom claims do Órbita a partir do token decodido.
 * Retorna null se o claim product:"orbita" não estiver presente (fail-closed).
 *
 * Firebase SDK expõe claims como um tipo opaco; usamos narrowing explícito
 * para não depender de `any` (ADR-003 §4 — fail-closed, nunca confiar no cliente).
 */
function extractOrbClaims(rawClaims: unknown): OrbClaims | null {
  if (!isRecord(rawClaims)) return null;
  if (rawClaims["product"] !== "orbita") return null;

  const tenantId = rawClaims["tenantId"];
  if (typeof tenantId !== "string" || tenantId.length === 0) return null;

  const role = rawClaims["role"];
  if (!isOrbRole(role)) return null;

  const rawAssigned = rawClaims["assignedTenantIds"];
  const assignedTenantIds: string[] = isStringArray(rawAssigned)
    ? rawAssigned
    : [tenantId];

  const rawModules = rawClaims["allowedModules"];
  const allowedModules: OrbModuleId[] = isStringArray(rawModules)
    ? (rawModules as OrbModuleId[])
    : [];

  const rawFacilities = rawClaims["allowedFacilities"];
  const allowedFacilities: string[] = isStringArray(rawFacilities)
    ? rawFacilities
    : [];

  const phi = rawClaims["phi"] === true;

  return {
    product: "orbita",
    tenantId,
    assignedTenantIds,
    role,
    allowedModules,
    allowedFacilities,
    phi,
  };
}

// ---------------------------------------------------------------------------
// Contexto
// ---------------------------------------------------------------------------

interface AuthContextValue {
  state: AuthState;
  /** Faz logout e limpa o estado de autenticação */
  signOut: () => Promise<void>;
  /** Envia e-mail de recuperação de senha */
  sendPasswordReset: (email: string) => Promise<void>;
  /**
   * Força refresh do token JWT e re-leitura de claims.
   * Útil após admin alterar claims de um usuário ativo.
   */
  refreshToken: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

// ---------------------------------------------------------------------------
// Provider
// ---------------------------------------------------------------------------

interface AuthProviderProps {
  children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [state, setState] = useState<AuthState>({ status: "loading" });
  const [currentUser, setCurrentUser] = useState<User | null>(null);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (firebaseUser) => {
      if (!firebaseUser) {
        setState({ status: "unauthenticated" });
        setCurrentUser(null);
        return;
      }

      setCurrentUser(firebaseUser);

      // Lê claims do token — sem forçar refresh na montagem inicial
      firebaseUser
        .getIdTokenResult()
        .then((tokenResult) => {
          // tokenResult.claims é ParsedToken — fazemos narrowing em extractOrbClaims (fail-closed)
          const claims = extractOrbClaims(tokenResult.claims);

          if (!claims) {
            setState({ status: "no-access", reason: "no-orbita-claim" });
            return;
          }

          if (!claims.tenantId) {
            setState({ status: "no-access", reason: "no-tenant" });
            return;
          }

          const orbUser: OrbUser = {
            uid: firebaseUser.uid,
            email: firebaseUser.email,
            displayName: firebaseUser.displayName,
            photoURL: firebaseUser.photoURL,
            claims,
          };

          setState({ status: "authenticated", user: orbUser });
        })
        .catch(() => {
          // Falha ao ler token → fail-closed
          setState({ status: "no-access", reason: "no-orbita-claim" });
        });
    });

    return unsubscribe;
  }, []);

  const signOut = async () => {
    await firebaseSignOut(auth);
    setState({ status: "unauthenticated" });
    setCurrentUser(null);
  };

  const sendPasswordReset = async (email: string) => {
    await sendPasswordResetEmail(auth, email);
  };

  const refreshToken = async () => {
    if (!currentUser) return;

    setState({ status: "loading" });
    try {
      const tokenResult = await currentUser.getIdTokenResult(true); // forçar refresh
      const claims = extractOrbClaims(tokenResult.claims);

      if (!claims) {
        setState({ status: "no-access", reason: "no-orbita-claim" });
        return;
      }

      const orbUser: OrbUser = {
        uid: currentUser.uid,
        email: currentUser.email,
        displayName: currentUser.displayName,
        photoURL: currentUser.photoURL,
        claims,
      };

      setState({ status: "authenticated", user: orbUser });
    } catch {
      setState({ status: "no-access", reason: "no-orbita-claim" });
    }
  };

  return (
    <AuthContext.Provider value={{ state, signOut, sendPasswordReset, refreshToken }}>
      {children}
    </AuthContext.Provider>
  );
}

// ---------------------------------------------------------------------------
// Hooks — exportados separadamente para evitar warning do react-refresh
// (arquivo mistura Provider + hooks, mas é padrão shadcn/ALADDIN)
// ---------------------------------------------------------------------------

// eslint-disable-next-line react-refresh/only-export-components -- padrão context: provider + hooks no mesmo arquivo
export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error("useAuth deve ser usado dentro de <AuthProvider>.");
  }
  return ctx;
}

// eslint-disable-next-line react-refresh/only-export-components -- padrão context
export function useOrbUser(): OrbUser | null {
  const { state } = useAuth();
  return state.status === "authenticated" ? state.user : null;
}
